import { css, Rule } from 'glamor'
import * as PropTypes from 'prop-types'
import * as React from 'react'

import { isKey, isElement } from '../../utils'
import { useTheme } from '../../theme'

import getStyles from './styles'

type FlagGutterSize = 'small' | 'medium' | 'large'

const gutterSizes: FlagGutterSize[] = ['small', 'medium', 'large']

interface IFlagProps {
  children?: React.ReactNode
  reversed?: boolean
  gutterSize?: FlagGutterSize
}

/**
 * Flag
 *
 * Flag object, used in conjunction with Flag.Img and Flag.Body
 *
 * @type { React.StatelessComponent<{ children, reversed, gutterSize }> }
 *
 * @prop { Bool } [reversed]
 *    - Iff true, the media image will be on the right hand side
 * @prop { String } [gutterSize]
 *    - One of small, medium, large
 *
 * @example
  import { Flag, FlagImg, FlagBody } from '../../ui-kit'
  <Flag>
    <FlagImg>
      <img src='..'>
    </FlagImg>
    <FlagBody>
      Lorem ipsum dolor sit amet, consectetur adipisicing elit.
    </FlagBody>
  </Flag>
*/
export const Flag: React.FC<IFlagProps> = ({ children, reversed = false, gutterSize = 'medium' }) => {
  const styles = getStyles(useTheme())
  const gutterKey = `gutter-size-${gutterSize}`
  return (
    <div
      {...css([
        styles.flag.base,
        reversed ? styles.flag.reversed : {},
        isKey(styles.flag, gutterKey) && styles.flag[gutterKey],
      ])}
    >
      {React.Children.map(children, child => (isElement(child) ? React.cloneElement(child, { gutterSize }) : child))}
    </div>
  )
}

Flag.propTypes = {
  children: PropTypes.node,
  reversed: PropTypes.bool,
  gutterSize: PropTypes.oneOf(gutterSizes),
}

interface IFlagImgProps {
  children?: React.ReactNode
  style?: Rule
  gutterSize?: FlagGutterSize
}

/**
 * FlagImg
 *
 * @type { React.StatelessComponent<{ children, gutterSize, style }> }
 *
 * @example
  import { FlagImg } from '../../ui-kit'
  <FlagImg>
    <img src='..'>
  </FlagImg>
*/
export const FlagImg: React.FC<IFlagImgProps> = ({ children, gutterSize = 'medium', style = {} }) => {
  const styles = getStyles(useTheme())
  const gutterKey = `gutter-size-${gutterSize}`
  return <div {...css([styles.img.base, isKey(styles.img, gutterKey) && styles.img[gutterKey], style])}>{children}</div>
}

FlagImg.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  gutterSize: PropTypes.oneOf(gutterSizes),
}

interface IFlagBodyProps {
  children?: React.ReactNode
  style?: Rule
  gutterSize?: FlagGutterSize
}

/**
 * FlagBody
 *
 * @type { React.StatelessComponent<{ children, gutterSize, style }> }
 *
 * @example
  import { FlagBody } from '../../ui-kit'
  <FlagBody>
    Lorem ipsum dolor sit amet, consectetur adipisicing elit.
  </FlagBody>
*/
export const FlagBody: React.FC<IFlagBodyProps> = ({ children, gutterSize = 'medium', style = {} }) => {
  const styles = getStyles(useTheme())
  const gutterKey = `gutter-size-${gutterSize}`
  return (
    <div {...css([styles.body.base, isKey(styles.body, gutterKey) && styles.body[gutterKey], style])}>{children}</div>
  )
}

FlagBody.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
  gutterSize: PropTypes.oneOf(gutterSizes),
}
